import React, { useCallback, useEffect, useState } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { io, Socket } from 'socket.io-client';
import api from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import { useI18n } from '../../i18n/useI18n';

type Props = {
  machineCode: string;
  userId: string;
};

const ACCENT = '#7c3aed';

const BackgroundRemovalDeviceToggle: React.FC<Props> = ({ machineCode, userId }) => {
  const { pages: t } = useI18n();
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const endpoint = `/admin/users/${encodeURIComponent(userId)}/machines/${encodeURIComponent(machineCode)}/background-removal`;

  const load = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setEnabled(res.data?.backgroundRemovalEnabled === true);
    } catch {
      setMessage(t.deviceMgmt.bgRemovalLoadError);
    }
  }, [endpoint, t.deviceMgmt.bgRemovalLoadError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    const mc = String(machineCode).trim().toUpperCase();
    socket.on('connect', () => socket.emit('join-machine-room', mc));
    socket.on('background-removal:enabled-changed', (data) => {
      if (String(data?.machineCode || '').toUpperCase() !== mc) return;
      if (typeof data?.backgroundRemovalEnabled === 'boolean') {
        setEnabled(data.backgroundRemovalEnabled);
      }
    });
    return () => {
      socket.emit('leave-machine-room', mc);
      socket.disconnect();
    };
  }, [machineCode]);

  const onToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSaving(true);
    setMessage('');
    const prev = enabled;
    setEnabled(checked);
    try {
      const res = await api.patch(endpoint, {
        backgroundRemovalEnabled: checked,
        source: 'admin',
      });
      setEnabled(res.data?.backgroundRemovalEnabled === true);
      setMessage(t.deviceMgmt.bgRemovalSaveSuccess);
    } catch (error: any) {
      setEnabled(prev);
      setMessage(error?.response?.data?.message || t.deviceMgmt.bgRemovalSaveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: { xs: 1.25, sm: 1.75 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(ACCENT, 0.14),
        bgcolor: alpha(ACCENT, 0.04),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', sm: '1rem' }, color: ACCENT }}>
          {t.deviceMgmt.bgRemovalTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
          {t.deviceMgmt.bgRemovalHint}
        </Typography>
        {message ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {message}
          </Typography>
        ) : null}
      </Box>
      <Switch checked={enabled} onChange={onToggle} disabled={saving} color="primary" />
    </Box>
  );
};

export default BackgroundRemovalDeviceToggle;
